<template>
  <main class="home-page" v-loading="loading">
    <section class="welcome-panel">
      <div class="welcome-copy">
        <p class="eyebrow">HAIKUN WATER QUALITY</p>
        <h1>{{ greeting }}，{{ displayName }}</h1>
        <p>欢迎回到水质检测管理系统。您可以在这里查看当前账号和已绑定设备。</p>
        <div class="welcome-actions">
          <el-button type="primary" @click="$router.push('/water-quality-data')">查看水质数据</el-button>
          <el-button plain @click="$router.push('/workbuddy')">WorkBuddy 授权</el-button>
        </div>
      </div>
      <div class="account-summary">
        <el-avatar :size="58">{{ avatarText }}</el-avatar>
        <div>
          <strong>{{ displayName }}</strong>
          <span>{{ roleName }}</span>
        </div>
        <i class="status-dot" aria-label="账号状态正常"></i>
      </div>
    </section>

    <section class="overview-grid">
      <article class="overview-card">
        <span class="overview-icon device-icon">设备</span>
        <div><strong>{{ deviceIds.length }}</strong><p>当前绑定设备</p></div>
      </article>
      <article class="overview-card">
        <span class="overview-icon role-icon">权限</span>
        <div><strong class="text-value">{{ roleName }}</strong><p>当前账号权限</p></div>
      </article>
      <article class="overview-card">
        <span class="overview-icon service-icon">服务</span>
        <div><strong class="text-value success-text">正常</strong><p>账号服务状态</p></div>
      </article>
    </section>

    <section class="detail-grid">
      <el-card shadow="never" class="info-card">
        <template #header>
          <div class="card-heading">
            <div><h2>个人信息</h2><p>当前登录账号的基础资料</p></div>
            <el-button text type="primary" :loading="refreshing" @click="loadHomeData">刷新</el-button>
          </div>
        </template>
        <div class="profile-list">
          <div class="profile-row">
            <span>用户名</span>
            <strong>{{ displayName }}</strong>
          </div>
          <div class="profile-row">
            <span>手机号</span>
            <div><strong>{{ profile.phone || '暂未填写' }}</strong><el-button v-if="profile.phone" text @click="copy(profile.phone)">复制</el-button></div>
          </div>
          <div class="profile-row">
            <span>账号身份</span>
            <el-tag effect="plain" type="success">{{ roleName }}</el-tag>
          </div>
        </div>
        <el-alert v-if="profileError" type="warning" :closable="false" show-icon title="个人信息暂时加载失败，请点击刷新重试。" />
      </el-card>

      <el-card shadow="never" class="info-card device-card">
        <template #header>
          <div class="card-heading">
            <div><h2>已绑定机器 ID</h2><p>仅显示当前账号正在关联的设备</p></div>
            <el-tag round>{{ deviceIds.length }} 台</el-tag>
          </div>
        </template>
        <BoundDeviceList v-if="deviceIds.length" :device-ids="deviceIds" @copy="copy" />
        <el-empty v-else-if="!deviceError" :image-size="72" description="当前账号暂未绑定设备" />
        <el-alert v-if="deviceError" type="warning" :closable="false" show-icon title="设备信息暂时加载失败，请点击刷新重试。" />
      </el-card>
    </section>
  </main>
</template>

<script>
import { ElMessage } from 'element-plus';
import { getCurrentProfile } from '@/api/user';
import { getMyDeviceIds } from '@/api/device';
import BoundDeviceList from './components/BoundDeviceList.vue';

export default {
  name: 'HomeView',
  components: { BoundDeviceList },
  data() {
    return {
      loading: true,
      refreshing: false,
      profile: {},
      deviceIds: [],
      profileError: false,
      deviceError: false,
    };
  },
  computed: {
    displayName() {
      return this.profile.nickName || '海鲲用户';
    },
    avatarText() {
      return this.displayName.slice(0, 1).toUpperCase();
    },
    greeting() {
      const hour = new Date().getHours();
      if (hour < 11) return '早上好';
      if (hour < 14) return '中午好';
      if (hour < 18) return '下午好';
      return '晚上好';
    },
    roleName() {
      const roles = { 0: '超级管理员', 1: '管理员', 2: '检测员' };
      const permission = this.profile.permission ?? Number(localStorage.getItem('permission'));
      return roles[permission] || '普通用户';
    },
  },
  mounted() {
    this.loadHomeData();
  },
  methods: {
    async loadHomeData() {
      this.refreshing = !this.loading;
      const [profileResult, deviceResult] = await Promise.allSettled([
        getCurrentProfile(),
        getMyDeviceIds(),
      ]);

      if (profileResult.status === 'fulfilled') {
        this.profile = profileResult.value.data || {};
        this.profileError = false;
      } else {
        this.profileError = true;
      }

      if (deviceResult.status === 'fulfilled') {
        this.deviceIds = Array.isArray(deviceResult.value.data) ? deviceResult.value.data : [];
        this.deviceError = false;
      } else {
        this.deviceError = true;
      }
      this.loading = false;
      this.refreshing = false;
    },
    async copy(value) {
      try {
        await navigator.clipboard.writeText(value);
        ElMessage.success('已复制');
      } catch (_) {
        ElMessage.warning('浏览器未允许复制，请手动选择文本');
      }
    },
  },
};
</script>

<style scoped>
.home-page {
  --brand: #0a7b73;
  --ink: #183042;
  --muted: #718391;
  min-height: 100%;
  color: var(--ink);
}

.welcome-panel {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  overflow: hidden;
  padding: 34px 38px;
  border: 1px solid #d7ebe8;
  border-radius: 18px;
  background: linear-gradient(125deg, #f7fffe 0%, #e8f7f4 58%, #d9eeec 100%);
  box-shadow: 0 14px 34px rgba(25, 91, 85, 0.08);
}

.welcome-panel::after {
  content: '';
  position: absolute;
  right: -80px;
  top: -130px;
  width: 320px;
  height: 320px;
  border: 54px solid rgba(255, 255, 255, 0.45);
  border-radius: 50%;
}

.welcome-copy, .account-summary { position: relative; z-index: 1; }
.eyebrow { margin: 0 0 9px; color: var(--brand); font-size: 12px; font-weight: 700; letter-spacing: 1.8px; }
.welcome-copy h1 { margin: 0; font-size: 30px; line-height: 1.35; }
.welcome-copy > p:not(.eyebrow) { margin: 10px 0 20px; color: #5f766f; line-height: 1.7; }
.welcome-actions { display: flex; gap: 10px; flex-wrap: wrap; }

.account-summary {
  display: flex;
  align-items: center;
  min-width: 250px;
  padding: 18px 20px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 10px 24px rgba(38, 102, 96, 0.09);
  backdrop-filter: blur(8px);
}
.account-summary :deep(.el-avatar) { flex-shrink: 0; background: var(--brand); font-size: 22px; }
.account-summary div { display: flex; flex: 1; flex-direction: column; gap: 6px; margin-left: 14px; }
.account-summary strong { font-size: 17px; }
.account-summary span { color: var(--muted); font-size: 13px; }
.status-dot { width: 9px; height: 9px; border: 3px solid #d9f3e5; border-radius: 50%; background: #20a769; }

.overview-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 16px; margin: 18px 0; }
.overview-card { display: flex; align-items: center; gap: 16px; padding: 20px 22px; border: 1px solid #e6edef; border-radius: 14px; background: #fff; }
.overview-icon { display: grid; place-items: center; width: 48px; height: 48px; flex-shrink: 0; border-radius: 13px; font-size: 12px; font-weight: 700; }
.device-icon { color: #09746d; background: #e2f5f2; }
.role-icon { color: #916300; background: #fff2d4; }
.service-icon { color: #31705c; background: #e5f4ec; }
.overview-card strong { display: block; font-size: 25px; line-height: 1.15; }
.overview-card .text-value { font-size: 18px; }
.success-text { color: #23845e; }
.overview-card p { margin: 5px 0 0; color: var(--muted); font-size: 13px; }

.detail-grid { display: grid; grid-template-columns: minmax(280px, 0.82fr) minmax(380px, 1.18fr); gap: 18px; }
.info-card { border: 1px solid #e3ebed; border-radius: 14px; }
.info-card :deep(.el-card__header) { padding: 20px 22px 16px; border-bottom-color: #edf1f2; }
.info-card :deep(.el-card__body) { padding: 10px 22px 22px; }
.card-heading { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.card-heading h2 { margin: 0; font-size: 18px; }
.card-heading p { margin: 5px 0 0; color: var(--muted); font-size: 12px; }
.profile-list { margin-bottom: 12px; }
.profile-row { display: flex; align-items: center; justify-content: space-between; min-height: 54px; border-bottom: 1px solid #eef2f3; }
.profile-row:last-child { border-bottom: 0; }
.profile-row > span { color: var(--muted); font-size: 13px; }
.profile-row > div { display: flex; align-items: center; gap: 4px; }
.profile-row strong { color: #273f50; font-size: 14px; }


@media (max-width: 960px) {
  .welcome-panel { align-items: stretch; flex-direction: column; }
  .account-summary { width: auto; }
  .detail-grid { grid-template-columns: 1fr; }
}

@media (max-width: 680px) {
  .welcome-panel { padding: 25px 22px; border-radius: 14px; }
  .welcome-copy h1 { font-size: 24px; }
  .overview-grid { grid-template-columns: 1fr; }
  .overview-card { padding: 16px 18px; }
  .detail-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>

